// The Implementation Specialist program's public page. It states the share and has
// an earnings model, but never a list price and nothing about what a pilot costs:
// those are in the terms Eric sends after an application. Applications go through the
// same route as the contact form, so they land in info@bossdrop.ai.
import React, { useState } from "react";
import SEO from "../components/SEO";
import PilotTimeline from "../components/PilotTimeline";
import { DEFAULT_INPUTS, EarningsModel, HeroExample, SHARE, SHARE_MONTHS } from "../components/SpecialistModel";
import { FAQ_GROUPS, FAQ_JSON_LD } from "../components/specialistFaqs";
import { TOOLS } from "../components/booking/BookingDetails";
import { API_BASE, HOST_EMAIL } from "../components/booking/bookingApi";
import "./specialists.css";

const NUMBERS = [
  { big: "100%", tone: "spGood", text: "of anything you charge your client. You set it, and BossDrop takes no cut." },
  { big: SHARE, tone: "spGood", text: "of what your client pays BossDrop, starting with their first payment." },
  { big: `${SHARE_MONTHS} months`, tone: "spCream", text: "of share from that first payment. Any team they add in that year counts too." },
  { big: "The 15th", tone: "spCream", text: "of the month after each payment clears is when you're paid, at the latest." },
];

const ICON_PATHS = {
  plug: <path d="M9 2v6M15 2v6M7 8h10v4a5 5 0 0 1-10 0V8zM12 17v5" />,
  target: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18M9 15.5l2 2 4-4" />
    </>
  ),
  chart: <path d="M4 4v16h16M8.5 16v-5M13 16V8M17.5 16v-3" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
};

function Icon({ name }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

const ROLE = [
  { icon: "plug", title: "Connect the tools", text: "Link BossDrop to the team's chat, task tool, email and calendar, with their permission." },
  { icon: "target", title: "Agree what counts", text: "Scope the team and set the success measures with the leader who said yes." },
  { icon: "calendar", title: "Hold the weekly check-in", text: "Stay close to the team and its leader for the whole 45 days." },
  { icon: "chart", title: "Prepare the day 45 readout", text: "Show the change against the baseline, so the leader can decide what comes next." },
];

// Outlook Calendar would show the Outlook logo a second time.
const LOGOS = TOOLS.filter((tool) => tool.key !== "outlook_calendar");

const FOR_WHO = [
  "Fractional COOs",
  "Chiefs of staff",
  "Operators",
  "Operations consultants",
  "Asana, monday.com and Jira rollout pros",
  "Advisers to growing companies",
];

// Eric, Nov 4: in the loop on the early rollouts, specialists on their own eventually,
// and handing a rollout over stays open for now.
const PATHS = [
  {
    rollout: "together",
    title: "Run it with Eric",
    text: "You lead the client relationship, and Eric works with you on all four jobs. You charge your client for your work and keep all of it. As you get the hang of it you take on more, until you run rollouts on your own.",
    earn: `Your fee + ${SHARE}`,
  },
  {
    rollout: "eric",
    title: "Hand it to Eric",
    text: "Rather only make the introduction? Tell Eric before the pilot starts and Eric does all four jobs. This is open for now, while BossDrop is early.",
    earn: SHARE,
  },
];

const EITHER_WAY = [
  "You bring the client. BossDrop doesn't supply leads today",
  "Eric hosts every demo",
  "A private Slack channel with Eric for anything you need",
  "A login that shows your link, your companies, what you've earned and what you've been paid",
  "No certification needed",
];

const STEPS = [
  {
    title: "Apply",
    text: "Two minutes, below. Eric reads every application, then sends you the terms, your link and your login.",
  },
  {
    title: "Bring your client",
    text: "Register the company in your dashboard so it's held for you, then introduce them to Eric or send your link.",
  },
  {
    title: "Run the rollout with Eric",
    text: "45 days with one team of 10 to 30 people. The client signs its pilot with BossDrop, and any fee of yours is between you and them.",
  },
  {
    title: "Get paid",
    text: "If they become a customer, you earn from their first payment. It all shows in your dashboard.",
  },
];

const RULES = [
  ["New companies only.", "Not a current or past customer, and not already talking to us."],
  [
    "Say you're paid.",
    "Tell people BossDrop pays you when you introduce them or post about it. If you consult for or advise the company, tell the person who decides, in writing, before they sign anything, and copy Eric.",
  ],
  [
    "No insiders.",
    "Employees, directors and owners of the company being introduced can't earn on it. Neither can government employees, and government bodies can't be introduced for a share.",
  ],
  ["No shortcuts.", "No coupon sites, no ads on the BossDrop name, no bought lists."],
  ["Only what's true.", "No promises about results, price or customers beyond what BossDrop has published."],
];

const NEXT = [
  ["Eric reads it", "Every application gets read, and you get a reply."],
  ["You get the terms", "With your link and your login, if it's a fit."],
  ["You bring your first client", "Register them in your dashboard so they're held for you."],
];

const SENT = `Thanks. Eric will reply from ${HOST_EMAIL}.`;
const FAILED = `That didn't send. Please email ${HOST_EMAIL} instead.`;

function goTo(id) {
  return (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };
}

function Head({ id, kicker, title, sub }) {
  return (
    <header className="spHead reveal" data-reveal>
      <div className="spKicker">{kicker}</div>
      <h2 className="h2" id={id}>
        {title}
      </h2>
      {sub ? <p className="sub">{sub}</p> : null}
    </header>
  );
}

export default function Specialists() {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);

  const changeInputs = (patch) => setInputs((current) => ({ ...current, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const fd = new FormData(form);
    // Left empty by people; a bot that fills it is told it worked and nothing goes out.
    if (fd.get("website")) {
      setStatus(SENT);
      form.reset();
      return;
    }
    const name = String(fd.get("name") || "").trim();
    const body = {
      // The route puts the name in its subject line, so this is what shows in the inbox.
      name: `Specialist application: ${name}`,
      email: fd.get("email"),
      message: [
        "Implementation Specialist application",
        "",
        `Name: ${name}`,
        `Email: ${fd.get("email")}`,
        `What they do: ${fd.get("role")}`,
        `LinkedIn: ${fd.get("linkedin") || "(not given)"}`,
        `Who they'd bring first: ${fd.get("first") || "(not given)"}`,
      ].join("\n"),
    };
    try {
      setSending(true);
      const res = await fetch(`${API_BASE}/send-general-inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setStatus(SENT);
        form.reset();
      } else {
        setStatus(FAILED);
      }
    } catch {
      setStatus(FAILED);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="page container specialists">
      <SEO
        title="BossDrop | Implementation Specialists"
        description={`Run BossDrop for your clients, working with the founder. Keep every dollar you charge and earn ${SHARE} of what they pay BossDrop for their first ${SHARE_MONTHS} months. For fractional COOs, operators and consultants.`}
        canonical="https://bossdrop.ai/specialists"
        url="https://bossdrop.ai/specialists"
        jsonLd={FAQ_JSON_LD}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://bossdrop.ai/" },
            { "@type": "ListItem", position: 2, name: "Implementation Specialists", item: "https://bossdrop.ai/specialists" },
          ],
        }}
      />

      <div className="spHero">
        <div className="spHeroCopy">
          <div className="spPill">
            <i aria-hidden="true" /> Implementation Specialist program
          </div>
          <h1 className="h1 spTitle">
            Run BossDrop for your clients.{" "}
            <span className="fx-ai">
              <span className="fx-ai-text">Keep every dollar you charge.</span>
            </span>
          </h1>
          <p className="spLead">
            Bring a team that's always chasing status, and you and Eric run its 45-day rollout together. Anything you
            charge is all yours, and you also earn {SHARE} of what that client pays BossDrop for their first{" "}
            {SHARE_MONTHS} months.
          </p>
          <ul className="spWho" aria-label="Who this is for">
            {FOR_WHO.map((who) => (
              <li key={who}>{who}</li>
            ))}
          </ul>
          <div className="spCtas">
            <a className="btn btnCream" href="#apply" onClick={goTo("apply")}>
              Apply
            </a>
            <a className="btn btnGhost" href="#the-math" onClick={goTo("the-math")}>
              Run your numbers
            </a>
          </div>
          <p className="spHeroNote">
            Two minutes to apply, and Eric reads every one.{" "}
            {/* A full page load, so the sign-in page starts with no analytics tags (public/index.html). */}
            <a href="/specialists/login">Specialist log in</a>
          </p>
        </div>
        <HeroExample inputs={inputs} onChange={changeInputs} onChangeNumbers={goTo("the-math")} />
      </div>

      <div className="spNumbers">
        {NUMBERS.map((item, i) => (
          <article className="spNumber reveal" data-reveal style={{ "--d": `${i * 70}ms` }} key={item.big}>
            <div className={`spNumberBig ${item.tone}`}>{item.big}</div>
            <p>{item.text}</p>
          </article>
        ))}
      </div>

      <section className="spSection" aria-labelledby="sp-role">
        <Head
          id="sp-role"
          kicker="The work"
          title="What each rollout takes"
          sub="Four jobs over 45 days with one team."
        />
        <div className="spRole">
          {ROLE.map((item, i) => (
            <article className="spRoleCard reveal" data-reveal style={{ "--d": `${i * 70}ms` }} key={item.title}>
              <span className="spIcon">
                <Icon name={item.icon} />
              </span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>

        <div className="bd-marquee spLogos reveal" data-reveal>
          <div className="bd-marquee-track">
            {[...LOGOS, ...LOGOS].map((tool, i) => (
              <span key={`${tool.key}-${i}`} aria-hidden={i >= LOGOS.length ? "true" : undefined}>
                <img src={tool.logo} alt="" className="toolLogo" width="18" height="18" loading="lazy" />
                {tool.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="spSection" aria-labelledby="sp-paths">
        <Head
          id="sp-paths"
          kicker="Working with Eric"
          title="You and Eric run it together"
          sub="Early on, Eric is in the loop on every rollout. In time, you run them on your own. Your share is the same either way."
        />
        <div className="spPaths">
          {PATHS.map((path, i) => (
            <article
              className={`spPath reveal${i === 0 ? " spPathMain" : ""}`}
              data-reveal
              style={{ "--d": `${i * 80}ms` }}
              key={path.rollout}
            >
              <h3>{path.title}</h3>
              <p>{path.text}</p>
              <div className="spPathEarn">
                <span>You earn</span>
                <strong>{path.earn}</strong>
                <a
                  href="#the-math"
                  onClick={(e) => {
                    changeInputs({ rollout: path.rollout });
                    goTo("the-math")(e);
                  }}
                >
                  See the numbers
                </a>
              </div>
            </article>
          ))}
        </div>
        <div className="spEither reveal" data-reveal>
          <h3>Either way</h3>
          <ul className="spChecks">
            {EITHER_WAY.map((line) => (
              <li key={line}>
                <span className="spCheck">
                  <Icon name="check" />
                </span>
                {line}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="spSection" aria-labelledby="sp-days">
        <Head
          id="sp-days"
          kicker="The rollout"
          title="What the 45 days look like"
          sub="BossDrop does the watching and the follow-up. You and Eric handle the setup, the weekly check-in and the readout."
        />
        <PilotTimeline
          start={{ when: "Before day 1", title: "Connect the tools and agree the measures" }}
          watch={{
            when: "Days 1 to 14",
            title: "BossDrop watches",
            text: "It sets the baseline and sends the weekly report. No nudges or escalations yet.",
          }}
          run={{
            when: "Days 15 to 45",
            title: "BossDrop runs on its own",
            text: "It follows up and escalates by itself. Any automation can go back to approval at any time.",
          }}
          weekly={{ when: "Every week", title: "Hold the check-in" }}
          end={{ when: "Day 45", title: "Prepare the readout, and the client decides" }}
        />
      </section>

      <section className="spSection" aria-labelledby="sp-steps">
        <Head id="sp-steps" kicker="How it works" title="Four steps to your first payout" />
        <ol className="spSteps">
          {STEPS.map((step, i) => (
            <li className="spStep reveal" data-reveal style={{ "--d": `${i * 90}ms` }} key={step.title}>
              <span className="spStepNum" aria-hidden="true">
                {i + 1}
              </span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="spSection spMath" id="the-math" aria-labelledby="sp-math">
        <Head
          id="sp-math"
          kicker="The math"
          title="Run your own numbers"
          sub="Your assumptions, in plain sight. BossDrop is early, so none of this comes from other specialists' results."
        />
        <EarningsModel inputs={inputs} onChange={changeInputs} />
        <p className="spFine reveal" data-reveal>
          Estimates, not promises. They assume {SHARE_MONTHS} paid months per client and every client starting in the
          same month, with any fee of yours paid up front, and they count neither your costs nor your taxes. Extra teams
          a client adds would count too, but aren't modeled. Your share follows what each client actually pays, and
          BossDrop pays nothing for an introduction, a demo or a pilot.
        </p>
      </section>

      <section className="spSection panel spRules reveal" data-reveal aria-labelledby="sp-rules">
        <h2 className="h2" id="sp-rules">
          The ground rules
        </h2>
        <ul className="spRulesList">
          {RULES.map(([lead, rest]) => (
            <li key={lead}>
              <strong>{lead}</strong> {rest}
            </li>
          ))}
        </ul>
        <p className="muted spRulesFoot">The full terms come with your link.</p>
      </section>

      <section className="spSection spFaq" id="faq" aria-labelledby="sp-faq">
        <header className="spFaqSide reveal" data-reveal>
          <div className="spKicker">FAQ</div>
          <h2 className="h2" id="sp-faq">
            Questions you'd ask Eric
          </h2>
          <p className="sub">
            Anything else? <a href={`mailto:${HOST_EMAIL}?subject=Implementation%20Specialist%20question`}>Email Eric</a>.
          </p>
        </header>
        <div className="bd-faq spFaqList reveal" data-reveal>
          {FAQ_GROUPS.map((group) => (
            <div className="spFaqGroup" key={group.title}>
              <h3>{group.title}</h3>
              {group.items.map(({ q, a }) => (
                <details key={q} name="specialist-faq">
                  <summary>{q}</summary>
                  <div className="faq-content">
                    <p className="muted">{a}</p>
                  </div>
                </details>
              ))}
            </div>
          ))}
        </div>
      </section>

      <div className="panel specialistsApply reveal" data-reveal id="apply">
        <div className="spApplySide">
          <div className="spKicker">Apply</div>
          <h2 className="h2">Become a specialist</h2>
          <ol className="spNext">
            {NEXT.map(([title, text], i) => (
              <li key={title}>
                <span className="spStepNum" aria-hidden="true">
                  {i + 1}
                </span>
                <div>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div>
          <form className="formGrid" onSubmit={handleSubmit}>
            <div className="field">
              <label htmlFor="specialist-name">Your name</label>
              <input id="specialist-name" name="name" required maxLength={120} autoComplete="name" placeholder="Jane Doe" />
            </div>
            <div className="field">
              <label htmlFor="specialist-email">Work email</label>
              <input
                id="specialist-email"
                name="email"
                type="email"
                required
                maxLength={200}
                autoComplete="email"
                placeholder="you@company.com"
              />
            </div>
            <div className="field">
              <label htmlFor="specialist-role">What do you do?</label>
              <input
                id="specialist-role"
                name="role"
                required
                maxLength={200}
                placeholder="Fractional COO for software companies"
              />
            </div>
            <div className="field">
              <label htmlFor="specialist-linkedin">LinkedIn (optional)</label>
              <input id="specialist-linkedin" name="linkedin" maxLength={200} placeholder="linkedin.com/in/janedoe" />
            </div>
            <div className="field wide">
              <label htmlFor="specialist-first">Who would you bring first? (optional)</label>
              <textarea
                id="specialist-first"
                name="first"
                rows="3"
                maxLength={1000}
                placeholder="A type of team is fine. No need to name anyone yet."
              />
            </div>
            <div className="specialistsHp" aria-hidden="true">
              <label htmlFor="specialist-website">Website</label>
              <input id="specialist-website" name="website" tabIndex={-1} autoComplete="off" />
            </div>
            <div className="ctaRow field wide">
              <button className="btn btnCream" type="submit" disabled={sending}>
                {sending ? "Sending..." : "Send application"}
              </button>
              <a className="btn btnGhost" href={`mailto:${HOST_EMAIL}?subject=Implementation%20Specialist%20application`}>
                Email Eric instead
              </a>
            </div>
          </form>
          <p className="formStatus" role="status">
            {status}
          </p>
        </div>
      </div>
    </section>
  );
}
